import React, { useState } from 'react';
import { Button } from 'shadcn/components/ui/button';
import { generateRoomId } from '../interview-session/live-interview/room-setup/lib/utils';
import { useSearchParams } from 'react-router-dom';
import { getAgentConversationConnectionDetails } from '../../api/api';
import { AgentConversationPopover } from './components/AgentConversationPopover';
import { Mic } from 'lucide-react';

interface ConnectionDetails {
  serverUrl: string;
  participantToken: string;
}

const keyframes = `
  @keyframes beam-sweep {
    0% {
      transform: translateX(-100%) skewX(-15deg);
    }
    40%, 100% {
      transform: translateX(300%) skewX(-15deg);
    }
  }
`;

const AgentConversationHome: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isStarting, setIsStarting] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails | null>(null);

  const meetingId = searchParams.get('meetingId') || 'aynj_uk_visa_intake';

  const startAgentConversation = async () => {
    setIsStarting(true);
    try {
      const roomName = generateRoomId();
      
      const response = await getAgentConversationConnectionDetails({
        roomName: roomName,
        participantName: 'User',
        metadata: {
          interviewee: {
            firstName: 'User',
            email: 'user@example.com',
          },
          interviewId: meetingId,
        },
      });
      setConnectionDetails(response);
      setIsPopoverOpen(true);
    } catch (error) {
      console.error('Error starting agent conversation:', error);
      // Optionally show an error to the user
    } finally {
      setIsStarting(false);
    }
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
    setConnectionDetails(null);
  };

  const getMeetingTitle = (id: string) => {
    switch (id) {
      case 'aynj_uk_visa_intake':
        return 'Talk to Visa Consultant';
      case 'sdr_interview':
        return 'Talk to SDR Agent';
      case 'aws_consultant_interview':
        return 'Talk to AWS Consultant';
      default:
        return 'The all-in-one Voice AI platform';
    }
  };

  const getButtonText = (id: string) => {
    switch (id) {
      case 'aynj_uk_visa_intake':
        return 'Talk to Visa Consultant';
      case 'sdr_interview':
        return 'Talk to SDR Agent';
      case 'aws_consultant_interview':
        return 'Talk to AWS Consultant';
      default:
        return 'Talk to LiveKit';
    }
  }


  return (
    <div className="w-full h-screen bg-black text-white flex flex-col items-center justify-center text-center p-4">
      <style>{keyframes}</style>
      <h1 className="text-5xl md:text-7xl font-bold mb-4">
        {getMeetingTitle(meetingId)}
      </h1>
      
      <Button
        onClick={startAgentConversation}
        disabled={isStarting}
        className="relative overflow-hidden bg-transparent border border-white/30 text-white font-bold text-lg px-8 py-6 rounded-lg transition-all duration-300 ease-in-out hover:bg-white/10 hover:border-white/50 disabled:opacity-50 flex items-center space-x-2"
      >
        <span 
            className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent"
            style={{ animation: 'beam-sweep 4s linear infinite' }}
        />
        <Mic size={20} className="relative" />
        <span className="relative">
          {isStarting ? 'Starting...' : getButtonText(meetingId)}
        </span>
      </Button>
      
      {isPopoverOpen && (
        <AgentConversationPopover 
          connectionDetails={connectionDetails}
          userChoices={{ audioEnabled: true, userName: 'User' }}
          onClose={handleClosePopover}
          meetingTitle={getMeetingTitle(meetingId)}
        />
      )}
    </div>
  );
};

export default AgentConversationHome; 